import { useEffect, useState } from "react";
import { Button, Checkbox, Form, Input, Modal, Popconfirm, Switch, Table, Tag, message } from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useManagementStore } from "@/stores/management";
import { orgService, type RoleInfo } from "@/services/organization";
import { useCardExtra } from "@/layouts/ManagementLayout";

const ALL_PERMISSIONS = [
  "AgentCreate", "AgentUpdate", "AgentDelete", "AgentView",
  "SessionCreate", "SessionDelete", "SessionSwitchModel",
  "ProviderCreate", "ProviderUpdate", "ProviderDelete",
  "OrgMemberInvite", "OrgMemberRemove", "OrgRoleManage",
  "OrgSsoConfig", "AgentVisibilityManage",
];

export function RoleManagement() {
  const { t } = useTranslation("management");
  const { context } = useManagementStore();
  const orgId = context?.type === "org" ? context.org.id : "";
  const [roles, setRoles] = useState<RoleInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<RoleInfo | null>(null);
  const [form] = Form.useForm();

  const loadRoles = async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      setRoles(await orgService.listRoles(orgId));
    } catch { setRoles([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadRoles(); }, [orgId]);

  const openCreate = () => {
    setEditingRole(null);
    form.resetFields();
    form.setFieldsValue({ permissions: ["AgentView", "SessionCreate", "SessionDelete"] });
    setModalOpen(true);
  };

  const openEdit = (role: RoleInfo) => {
    setEditingRole(role);
    form.setFieldsValue({ name: role.name, permissions: role.permissions, is_default: role.is_default });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    try {
      if (editingRole) {
        await orgService.updateRole(orgId, editingRole.id, values);
      } else {
        await orgService.createRole(orgId, values);
      }
      message.success(t("common:success"));
      setModalOpen(false);
      loadRoles();
    } catch { message.error(t("common:error")); }
  };

  const handleDelete = async (id: string) => {
    try {
      await orgService.deleteRole(orgId, id);
      message.success(t("common:success"));
      loadRoles();
    } catch (e) { message.error(e instanceof Error ? e.message : t("common:error")); }
  };

  const columns = [
    { title: t("roles.table.name"), dataIndex: "name", key: "name" },
    {
      title: t("roles.table.permissions"), dataIndex: "permissions", key: "permissions",
      render: (perms: string[]) => (
        <span>{perms.length > 3 ? `${perms.slice(0, 3).join(", ")}... (+${perms.length - 3})` : perms.join(", ")}</span>
      ),
    },
    {
      title: t("roles.table.default"), dataIndex: "is_default", key: "is_default",
      render: (v: boolean) => v ? <Tag color="blue">Default</Tag> : null,
    },
    {
      title: t("common:action"), key: "action", width: 100,
      render: (_: unknown, record: RoleInfo) => (
        <span style={{ display: "flex", gap: 4 }}>
          <Button type="text" icon={<EditOutlined />} size="small" onClick={() => openEdit(record)} />
          <Popconfirm title={t("roles.delete.confirm")} onConfirm={() => handleDelete(record.id)}>
            <Button type="text" danger icon={<DeleteOutlined />} size="small" />
          </Popconfirm>
        </span>
      ),
    },
  ];

  useCardExtra(
    <Button type="primary" size="small" icon={<PlusOutlined />} onClick={openCreate}>{t("roles.create")}</Button>,
    [t],
  );

  return (
    <>
      <Table dataSource={roles} columns={columns} rowKey="id" loading={loading} size="middle" />
      <Modal
        title={editingRole ? t("common:edit") : t("roles.create")}
        open={modalOpen}
        onOk={handleSubmit}
        onCancel={() => setModalOpen(false)}
        width={520}
      >
        <Form form={form} layout="vertical">
          <Form.Item label={t("roles.table.name")} name="name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label={t("roles.table.permissions")} name="permissions" rules={[{ required: true }]}>
            <Checkbox.Group options={ALL_PERMISSIONS} style={{ display: "flex", flexWrap: "wrap", gap: "4px 0" }} />
          </Form.Item>
          <Form.Item label={t("roles.table.default")} name="is_default" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
